import { PageController } from '@client/components/_core/layout/PageController';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { AuthorisedAppsManager } from '@client/components/authorised-apps/AuthorisedAppsManager';
import { NotFound } from '@client/components/NotFound';
import { useOrg } from '@client/hooks/business/org/useOrg';
import { useUser } from '@client/hooks/business/user/useUser';
import { useAreFeatureFlagsSettled, useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { createFileRoute } from '@tanstack/react-router';
import { KeyRound } from 'lucide-react';

/**
 * /admin-console/authorised-apps
 *
 * The system-console counterpart of `/console/$orgId/user-settings/authorised-apps`. An admin
 * operating in the admin console has `ctx.user.orgId === -1` (SYSTEM_CONSOLE), so the shared
 * `AuthorisedAppsManager` lists the PATs that admin minted against the system org. Reached from the
 * top-bar user menu (which targets this route when in the admin console), not the main admin nav.
 *
 * `variant="systemConsole"` replaces the tenant `/mcp` walkthrough (whose consent step denies the
 * system console outside dev/staging) with the operator surfaces `/mcp-admin` and `/mcp-ops` and
 * their mint flows — see `SystemConsoleMcpSurfaces`.
 */

export const Route = createFileRoute('/admin-console/authorised-apps')({
    staticData: {
        ui: {
            icon: KeyRound,
            title: 'Authorised apps',
            widthVariant: 'compact',
            menuDescription: 'Operator MCP tokens minted against the system console',
        },
        // Declared so the route registry records the gate (menus and the AI navigation tool
        // hide the page while the flag is off) — matching the tenant counterpart.
        featureFlag: 'enable-mcp-server',
    },
    component: AdminAuthorisedAppsPage,
});

function AdminAuthorisedAppsPage() {
    const featureEnabled = useFeatureFlag('enable-mcp-server', false);
    const user = useUser({ throwOnUndefined: false });
    const org = useOrg({ throwOnUndefined: false });
    // `useFeatureFlag` reads `false` for both "off" and "not evaluated yet". The admin-console
    // tree carries no `RouteAccessGuard`, so this route has to make the same distinction itself:
    // PostHog re-evaluates flags after initialisation and again after `identifyUser()`, and
    // during either window the map is empty (or reduced to the server bootstrap's maintenance
    // keys — CUR-891). Acting on that transient `false` used to bounce the page to `/not-found`
    // for good, even though the flag was on — the Checkly "Admin console" suite caught it as a
    // 404 mid-test. Treat the flag as unknown until evaluation has settled for this identity.
    const areFeatureFlagsSettled = useAreFeatureFlagsSettled({
        userId: user?.id.toString(),
        orgId: org?.id.toString(),
    });

    if (!areFeatureFlagsSettled) {
        return <Loading size="large" className="flex-1" />;
    }

    if (!featureEnabled) {
        // Render the 404 in place (as `RouteAccessGuard` does) rather than navigating away.
        return <NotFound />;
    }

    return (
        <PageController route={Route}>
            <AuthorisedAppsManager variant="systemConsole" />
        </PageController>
    );
}
